import React, { useMemo, useState, useCallback } from 'react';
import DataTable from '../common/DataTable';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ordersService from '../../services/ordersService';
import { USER_ROLES } from '../../utils/constants';


const InvoiceSalesTable = ({
  sales = [],
  loading = false,
  selectedSales = [],
  onSelectSale,
  onSelectAll,
  userRole
}) => {
  const { t } = useTranslation();
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null);
  // Who can reprint an invoice from any row, regardless of order status —
  // mirrors the reprint affordance on the Delivery Challan Printed page.
  const canReprintInvoice = [
    USER_ROLES.ADMIN,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.ACCOUNT_USER,
    USER_ROLES.SALES_STAFF,
    USER_ROLES.OPERATIONS_MANAGER,
  ].includes(userRole);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return t('common.symbols.emDash');
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  }, [t]);

  const handlePrintInvoice = useCallback(async (orderOrId) => {
    const id = typeof orderOrId === 'object' ? (orderOrId.order_id || orderOrId.id || orderOrId.orderId) : orderOrId;
    if (!id) return;
    setPrintingInvoiceId(id);
    let order = null;
    try {
      const res = await ordersService.getOrderById(id, { force: true });
      if (res && res.success) order = res.data;
    } catch (e) { }

    if (!order && typeof orderOrId === 'object') order = orderOrId;
    if (!order) {
      setPrintingInvoiceId(null);
      alert(t('common.messages.unableToLoadOrderForPrinting'));
      return;
    }

    const safe = (v) => (v === null || typeof v === 'undefined' ? '' : String(v));
    const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
    const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
    const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const items = Array.isArray(order.items) ? order.items.filter(it => it && Object.keys(it).length > 0) : [];

    // HSN Summary Calculation
    const hsnSummaryMap = items.reduce((acc, it) => {
      const hsn = safe(it.hsn_code) || 'N/A';
      if (!acc[hsn]) {
        acc[hsn] = { qty: 0, amount: 0, gst: Number(it.gst_percentage) || 0, unit: safe(it.unit) };
      }
      acc[hsn].qty += Math.ceil(Number(it.quantity) || 0);
      acc[hsn].amount += (Number(it.price) || 0) * (Number(it.quantity) || 0);
      return acc;
    }, {});

    const itemsRows = items.map((it, idx) => {
      const actualPieces = it.actual_pieces || '';
      return `
      <tr>
        <td>${idx + 1}</td>
        <td>${safe(it.size_name || it.product_name)}</td>
        <td>${safe(it.hsn_code)}</td>
        <td>${actualPieces}</td>
        <td>${safe(Math.ceil(parseFloat(it.pending_order || 0)))} ${safe(it.unit)}</td>
        <td>${safe(Math.ceil(parseFloat(it.actual_weight || 0)))} ${safe(it.unit)}</td>
        <td>${safe(it.gst_percentage)}</td>
        <td class="text-right">₹ ${Number(it.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td class="text-right">₹ ${Number(it.gst_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td class="text-right">₹ ${Number(it.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>`;
    }).join('\n');

    const hsnRows = Object.entries(hsnSummaryMap).map(([hsn, data]) => {
      const gstAmt = (data.amount * data.gst) / 100;
      return `
      <tr>
        <td>${hsn}</td>
        <td>${Math.ceil(parseFloat(data.qty || 0))} ${data.unit}</td>
        <td>₹ ${Math.round(data.amount).toLocaleString('en-IN')}</td>
        <td>${data.gst}</td>
        <td>₹ ${gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td>₹ ${(data.amount + gstAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>`;
    }).join('\n');

    // Use actual values from API instead of calculating
    const totalItemAmount = Number(order.subtotal) || 0;
    const loading = Number(order.total_loading_cost) || 0;
    const loadingGst = Number(order.loading_cost_gst_amount) || 0;
    const freight = Number(order.freight) || 0;
    const freightGst = Number(order.freight_gst_amount) || 0;
    const totalGst = Number(order.total_gst_amount) || 0;
    const netTotal = Number(order.total_amount) || 0;

    // Freight type billing logic
    const freightTypeInv = order.freight_type || '';
    const isFreightChargeableInv = freightTypeInv === 'freight_chargeable';
    const isFreightToPayInv = freightTypeInv === 'free_delivery_freight_to_pay';
    const showFreightInv = !!freightTypeInv;
    const freightPricingTypeInv = order.freight_pricing_type || '';
    const freightLabelInv = isFreightChargeableInv
      ? `Freight Chargeable${freightPricingTypeInv === 'per_kg' ? ' (Per KG)' : freightPricingTypeInv === 'fixed' ? ' (Fixed)' : ''}`
      : isFreightToPayInv ? 'Freight To Pay'
      : freightTypeInv === 'ex_warehouse' ? 'Ex Warehouse'
      : freightTypeInv === 'free_delivery_by_supplier' ? 'Free by Supplier'
      : freightTypeInv === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
      : 'Freight';
    const freightValueInv = isFreightToPayInv
      ? `- ₹ ${freight.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
      : `₹ ${freight.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    const freightColorInv = isFreightToPayInv ? 'color: #059669;' : (!isFreightChargeableInv ? 'color: #9ca3af;' : '');

    const companyDetailsHtml = [
      process.env.REACT_APP_COMPANY_ADDRESS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '',
      (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '',
      process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : '',
    ].filter(Boolean).join('');

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Delivery Challan - ${safe(order.order_number)}</title>
  <style>
    @page { 
      size: A5;
      margin: 8mm;
    }
    * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
    body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
    .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
    .print-container { max-width: 148mm; margin: 0 auto; padding: 8mm; }
    .company-header { text-align: center; font-size: 16px; font-weight: 800; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 1px; color: #000; }
    .title { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; margin-bottom: 8px; font-size: 8px; }
    .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 1px; }
    .header-label { font-weight: 600; min-width: 50px; }
    
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 7px; border: 1px solid #ddd; }
    th, td { padding: 2px 3px; text-align: left; border: 1px solid #ddd; }
    th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 6px; letter-spacing: 0.3px; }
    .text-right { text-align: right; }
    
    .footer-section { display: grid; grid-template-columns: 1.5fr 1fr; gap: 8px; margin-top: 6px; align-items: flex-start; font-size: 7px; }
    .total-qty-row { font-size: 8px; font-weight: 700; margin-bottom: 4px; }
    .remarks-box { font-size: 8px; margin-top: 6px; padding: 4px 6px; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 2px; color: #92400e; }
    .remarks-label { font-weight: 700; color: #b45309; margin-bottom: 2px; display: block; text-transform: uppercase; font-size: 7px; }
    
    .hsn-summary-title { font-size: 8px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; color: #444; }
    .hsn-table td, .hsn-table th { padding: 2px 3px; font-size: 7px; }
    .hsn-table .total-row td { border-top: 2px solid #555; font-weight: 700; background: #fcfcfc; }
    
    .summary-table { width: 100%; border: none; }
    .summary-table td { padding: 2px 0; border: none; font-size: 8px; }
    .summary-table .label { color: #555; }
    .summary-table .value { text-align: right; font-weight: 600; }
    .summary-table .divider { border-top: 1px solid #ddd; margin: 4px 0; height: 1px; }
    .summary-table .intermediate-total td { font-weight: 700; padding-top: 4px; }
    .summary-table .gst-row td { color: #444; font-size: 7px; padding: 2px 0; }
    .summary-table .net-total-row td { border-top: 2px solid #333; font-weight: 800; font-size: 9px; padding-top: 4px; }
    
    @media print { 
      .no-print { display: none !important; } 
      .print-container { padding: 0; }
      body { font-size: 9px; }
    }
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print Delivery Challan</button></div>
  <div class="print-container">
    ${!order.gst_included ? `
      <div class="company-header">${process.env.REACT_APP_COMPANY_NAME}, ${process.env.REACT_APP_COMPANY_CITY}</div>
      ${companyDetailsHtml}
      <div class="title">Delivery Challan</div>
    ` : `
      <div class="title">Delivery Challan</div>
    `}
    
    <div class="header-grid">
      ${order.customer_name ? `<div class="header-field"><span class="header-label">Name:</span> <span>${safe(order.customer_name)}</span></div>` : ''}
      ${order.date ? `<div class="header-field"><span class="header-label">Date:</span> <span>${fmt(order.date)}</span></div>` : ''}
      
      ${order.delivery_location_name ? `<div class="header-field"><span class="header-label">Delivery @:</span> <span>${safe(order.delivery_location_name)}</span></div>` : ''}
      ${order.challan_number ? `<div class="header-field"><span class="header-label">Challan Serial:</span> <span>${safe(order.challan_number)}</span></div>` : ''}
      ${order.invoice_number ? `<div class="header-field"><span class="header-label">Invoice Number:</span> <span>${safe(order.invoice_number)}</span></div>` : ''}
      
      ${(order.created_at || order.date) ? `<div class="header-field"><span class="header-label">Time Created:</span> <span>${fmtTime(order.created_at || order.date)}</span></div>` : ''}
      <div class="header-field"><span class="header-label">Time Print:</span> <span>${nowTime}</span></div>
      
      ${order.vehicle_number ? `<div class="header-field"><span class="header-label">Truck Number:</span> <span>${safe(order.vehicle_number)}</span></div>` : ''}
      ${order.driver_contact ? `<div class="header-field"><span class="header-label">Driver Contact:</span> <span>${safe(order.driver_contact)}</span></div>` : ''}
      
      ${order.empty_truck_weight ? `<div class="header-field"><span class="header-label">Empty Truck Weight:</span> <span>${safe(order.empty_truck_weight)} KG</span></div>` : ''}
      ${order.final_truck_weight ? `<div class="header-field"><span class="header-label">Final Truck Weight:</span> <span>${safe(order.final_truck_weight)} KG</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width: 40px;">Sr.</th>
          <th>Size</th>
          <th style="width: 80px;">HSN</th>
          <th style="width: 60px;">Pcs</th>
          <th style="width: 100px;">Qty</th>
          <th style="width: 100px;">Actual Qty</th>
          <th style="width: 60px;">IGST</th>
          <th class="text-right" style="width: 100px;">Rate</th>
          <th class="text-right" style="width: 120px;">GST Amount</th>
          <th class="text-right" style="width: 120px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="footer-section">
      <div class="left-col">
        <div class="total-qty-row">Total Quantity: ${safe(Math.ceil(parseFloat(order.total_weight || 0)))} KG</div>
        
        ${order.remark ? `
        <div class="remarks-box">
          <span class="remarks-label">Remarks:</span>
          <div style="white-space: pre-wrap;">${safe(order.remark)}</div>
        </div>
        ` : ''}
        
        <div class="hsn-summary">
          <div class="hsn-summary-title">HSN Summary</div>
          <table class="hsn-table">
            <thead>
              <tr>
                <th>HSN Code</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>GST %</th>
                <th>GST Amount</th>
                <th>Total Inc GST</th>
              </tr>
            </thead>
            <tbody>
              ${hsnRows}
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-col">
        <table class="summary-table">
          <tr>
            <td class="label">Subtotal</td>
            <td class="value">₹ ${totalItemAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
          ${loading && parseFloat(loading) > 0 ? `
          <tr>
            <td class="label">Loading</td>
            <td class="value">₹ ${loading.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          ${loadingGst && parseFloat(loadingGst) > 0 ? `
          <tr>
            <td class="label" style="padding-left: 10px; font-size: 7px;">Loading GST</td>
            <td class="value" style="font-size: 7px;">₹ ${loadingGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          ${showFreightInv ? `
          <tr>
            <td class="label" style="${freightColorInv}">${freightLabelInv}</td>
            <td class="value" style="${freightColorInv}">${freightValueInv}</td>
          </tr>
          ${isFreightChargeableInv && freightGst > 0 ? `
          <tr>
            <td class="label" style="padding-left: 10px; font-size: 7px;">Freight GST</td>
            <td class="value" style="font-size: 7px;">₹ ${freightGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}` : ''}
          ${order.total_sub_order_expenses && parseFloat(order.total_sub_order_expenses) > 0 ? `
          <tr>
            <td class="label">Expenses</td>
            <td class="value">₹ ${parseFloat(order.total_sub_order_expenses).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          ${order.total_sub_order_discount && parseFloat(order.total_sub_order_discount) > 0 ? `
          <tr>
            <td class="label">Discount</td>
            <td class="value">- ₹ ${parseFloat(order.total_sub_order_discount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          ${totalGst && parseFloat(totalGst) > 0 ? `
          <tr class="gst-row">
            <td class="label">Total GST = IGST</td>
            <td class="value">₹ ${totalGst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>` : ''}
          <tr class="net-total-row">
            <td class="label">Total (with GST)</td>
            <td class="value">₹ ${netTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      setPrintingInvoiceId(null);
      alert(t('common.messages.unableToOpenPrintWindow'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    try { w.document.title = ''; } catch (e) { }
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } finally { setPrintingInvoiceId(null); } }, 300);
  }, [t]);

  const selectionColumn = useMemo(() => ({
    id: 'selection',
    sortable: false,
    width: '40px',
    renderHeader: () => {
      const currentPageIds = sales.map(s => s.id);
      const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedSales.includes(id));
      return (
        <input 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          checked={isAllSelected}
          onChange={(e) => onSelectAll?.(e.target.checked, currentPageIds)} 
        />
      );
    },
    renderCell: (s) => {
      const id = s.id;
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <input 
            type="checkbox" 
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            checked={selectedSales.includes(id)}
            onChange={(e) => onSelectSale?.(id, e.target.checked)} 
          />
        </div>
      );
    },
    skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
  }), [sales, selectedSales, onSelectAll, onSelectSale]);

  const baseColumns = useMemo(() => [
    {
      id: 'date',
      label: t('invoiceSales.table.columns.date'),
      sortable: true,
      sortAccessor: (r) => (r.date ? new Date(r.date).getTime() : 0),
      renderCell: (r) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{formatDate(r.date)}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'invoice_number',
      label: t('invoiceSales.table.columns.invoiceNumber'),
      sortable: true,
      sortAccessor: (r) => r.invoice_number || '',
      renderCell: (r) => <span className="text-sm">{r.invoice_number || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-36 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'order_number',
      label: t('invoiceSales.table.columns.orderNumber'),
      sortable: true,
      sortAccessor: (r) => r.order_number || '',
      renderCell: (r) => <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.order_number || t('common.symbols.emDash')}</div>,
      skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'tally_name',
      label: t('invoiceSales.table.columns.tallyName'),
      sortable: true,
      sortAccessor: (r) => (r.tally_name || '').toLowerCase(),
      renderCell: (r) => <span className="text-sm">{r.tally_name || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-36 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'vehicle_number',
      label: t('invoiceSales.table.columns.vehicleNumber'),
      sortable: true,
      sortAccessor: (r) => (r.vehicle_number || '').toLowerCase(),
      renderCell: (r) => <span className="text-sm">{r.vehicle_number || t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'actual_quantity',
      label: "Total Quantity",
      sortable: true,
      sortAccessor: (r) => (r.actual_quantity ? parseFloat(r.actual_quantity) : 0),
      renderCell: (r) => <span className="text-sm">{r.actual_quantity != null ? `${r.actual_quantity}` : t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      align: 'right',
    },
    {
      id: 'total_amount',
      label: t('invoiceSales.table.columns.totalAmount'),
      sortable: true,
      sortAccessor: (r) => (r.total_amount ? parseFloat(r.total_amount) : 0),
      renderCell: (r) => <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{r.total_amount != null ? `${t('common.symbols.currency')}${Number(r.total_amount).toFixed(2)}` : t('common.symbols.emDash')}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      align: 'right',
    },
    {
      id: 'remark',
      label: t('invoiceSales.table.columns.remark'),
      sortable: true,
      sortAccessor: (r) => (r.remark || '').toLowerCase(),
      renderCell: (r) => {
        if (!r.remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
        return (
          <div 
            className="text-[11px] font-medium break-words leading-relaxed min-w-[200px] px-2 py-1.5 rounded border" 
            title={r.remark} 
            style={{ 
              backgroundColor: 'rgba(254, 243, 199, 0.5)', 
              color: '#92400e', 
              borderColor: 'rgba(217, 119, 6, 0.2)',
              whiteSpace: 'pre-wrap'
            }}
          >
            {r.remark}
          </div>
        );
      },
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
  ], [t, formatDate]);

  const emptyState = useMemo(() => ({
    message: t('invoiceSales.table.emptyState.message'),
  }), [t]);

  // Add actions column separately so it can reference local handlers
  const actionsColumn = useMemo(() => ({
    id: 'actions',
    label: t('common.table.actions'),
    align: 'center',
    sortable: false,
    renderCell: (r) => {
      const id = r.order_id || r.id || r.orderId || r._id;
      return (
        <div className="flex items-center justify-center space-x-2">
          {canReprintInvoice && (
            <button
              onClick={() => handlePrintInvoice(id)}
              disabled={printingInvoiceId === id}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: '#f3f4f6' }}
              title={t('orders.table.tooltips.printInvoice')}
            >
              {printingInvoiceId === id ? (
                <FontAwesomeIcon icon={faSpinner} spin className="text-sm" />
              ) : (
                <FontAwesomeIcon icon={faPrint} className="text-sm" />
              )}
            </button>
          )}
        </div>
      );
    },
    skeleton: (
      <div className="flex items-center justify-center space-x-2">
        <div className="w-8 h-8 bg-gray-300 rounded-lg" />
      </div>
    ),
  }), [handlePrintInvoice, printingInvoiceId, canReprintInvoice, t]);

  const columns = useMemo(() => [selectionColumn, ...baseColumns, actionsColumn], [selectionColumn, baseColumns, actionsColumn]);

  return (
    <div className="bg-white mt-2 rounded-lg shadow-sm border border-gray-200">
      <DataTable
        data={Array.isArray(sales) ? sales : []}
        columns={columns}
        loading={loading}
        keyField="id"
        enablePagination={false}
        defaultSort={{ field: 'order_number', direction: 'asc' }}
        emptyState={emptyState}
      />
    </div>
  );
};

export default InvoiceSalesTable;
